import {
  Injectable,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import {
  CustomerNotificationItemDto,
  CustomerNotificationsApi,
  CustomerNotificationsResponse,
} from './customer-notifications.api';
import { AuthService } from './auth.service';

export type CustomerNotificationFilter = 'all' | 'unread' | 'archived';
export type CustomerNotificationTone = 'order' | 'coupon' | 'gift' | 'delivery';

export interface CustomerNotificationItem extends CustomerNotificationItemDto {
  tone: CustomerNotificationTone;
  timeLabel: string;
}

interface NotificationActionDefinition {
  referenceType: string;
  buildTarget: (referenceId: string) => string[];
}

const DEFAULT_PAGE_SIZE = 32;
const REQUEST_INTERVAL_MS = 1000;
const SYRIA_UTC_OFFSET_MS = 3 * 60 * 60 * 1000;
const ACTION_MAP: Record<string, NotificationActionDefinition> = {
  'customer-order-details': {
    referenceType: 'order',
    buildTarget: (referenceId) => ['/order-details', referenceId],
  },
  'customer-support-ticket-details': {
    referenceType: 'customer-support-ticket',
    buildTarget: (referenceId) => ['/ticket-details', referenceId],
  },
  'customer-refund-request-details': {
    referenceType: 'refund-request',
    buildTarget: (referenceId) => ['/profile/refunds', referenceId],
  },
};

@Injectable({ providedIn: 'root' })
export class CustomerNotificationsStore {
  private readonly api = inject(CustomerNotificationsApi);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  readonly activeItems = signal<CustomerNotificationItem[]>([]);
  readonly archivedItems = signal<CustomerNotificationItem[]>([]);
  readonly unreadCount = signal(0);

  readonly isActiveLoading = signal(false);
  readonly isArchivedLoading = signal(false);
  readonly isUnreadCountLoading = signal(false);
  readonly isMutating = signal(false);

  readonly activeLoaded = signal(false);
  readonly archivedLoaded = signal(false);

  readonly activePage = signal(1);
  readonly archivedPage = signal(1);
  readonly activeTotalCount = signal(0);
  readonly archivedTotalCount = signal(0);

  readonly activeError = signal('');
  readonly archivedError = signal('');

  private lastActiveRequestAt = 0;
  private lastArchivedRequestAt = 0;
  private lastUnreadCountRequestAt = 0;

  readonly activeHasMore = computed(
    () => this.activeItems().length < this.activeTotalCount(),
  );
  readonly archivedHasMore = computed(
    () => this.archivedItems().length < this.archivedTotalCount(),
  );
  readonly previewItems = computed(() => this.activeItems().slice(0, 4));

  constructor() {
    effect(() => {
      if (this.auth.isLoggedIn()) {
        untracked(() => this.ensureReady());
      } else {
        this.resetState();
      }
    });
  }

  ensureReady() {
    if (!this.activeLoaded()) {
      this.refreshActive();
    } else if (!this.isUnreadCountLoading()) {
      this.refreshUnreadCount();
    }
  }

  ensureArchivedReady() {
    if (!this.archivedLoaded()) {
      this.refreshArchived();
    }
  }

  refreshActive() {
    if (
      !this.auth.isLoggedIn() ||
      this.isActiveLoading() ||
      !this.canRequest('active')
    ) {
      return;
    }

    this.lastActiveRequestAt = Date.now();
    this.isActiveLoading.set(true);
    this.activeError.set('');

    this.api
      .list(1, DEFAULT_PAGE_SIZE, false)
      .pipe(finalize(() => this.isActiveLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.setPageState('active', response, true);
          this.refreshUnreadCount();
        },
        error: () => {
          this.activeError.set('notifications.states.error');
        },
      });
  }

  loadMoreActive() {
    if (
      !this.auth.isLoggedIn() ||
      this.isActiveLoading() ||
      !this.activeHasMore() ||
      !this.canRequest('active')
    ) {
      return;
    }

    this.lastActiveRequestAt = Date.now();
    this.isActiveLoading.set(true);
    this.activeError.set('');

    this.api
      .list(this.activePage() + 1, DEFAULT_PAGE_SIZE, false)
      .pipe(finalize(() => this.isActiveLoading.set(false)))
      .subscribe({
        next: (response) => this.setPageState('active', response, false),
        error: () => {
          this.activeError.set('notifications.states.error');
        },
      });
  }

  refreshArchived() {
    if (
      !this.auth.isLoggedIn() ||
      this.isArchivedLoading() ||
      !this.canRequest('archived')
    ) {
      return;
    }

    this.lastArchivedRequestAt = Date.now();
    this.isArchivedLoading.set(true);
    this.archivedError.set('');

    this.api
      .list(1, DEFAULT_PAGE_SIZE, true)
      .pipe(finalize(() => this.isArchivedLoading.set(false)))
      .subscribe({
        next: (response) => this.setPageState('archived', response, true),
        error: () => {
          this.archivedError.set('notifications.states.error');
        },
      });
  }

  loadMoreArchived() {
    if (
      !this.auth.isLoggedIn() ||
      this.isArchivedLoading() ||
      !this.archivedHasMore() ||
      !this.canRequest('archived')
    ) {
      return;
    }

    this.lastArchivedRequestAt = Date.now();
    this.isArchivedLoading.set(true);
    this.archivedError.set('');

    this.api
      .list(this.archivedPage() + 1, DEFAULT_PAGE_SIZE, true)
      .pipe(finalize(() => this.isArchivedLoading.set(false)))
      .subscribe({
        next: (response) => this.setPageState('archived', response, false),
        error: () => {
          this.archivedError.set('notifications.states.error');
        },
      });
  }

  refreshUnreadCount() {
    if (
      !this.auth.isLoggedIn() ||
      this.isUnreadCountLoading() ||
      !this.canRequest('unread')
    ) {
      return;
    }

    this.lastUnreadCountRequestAt = Date.now();
    this.isUnreadCountLoading.set(true);

    this.api
      .getUnreadCount()
      .pipe(finalize(() => this.isUnreadCountLoading.set(false)))
      .subscribe({
        next: (response) => this.unreadCount.set(response.count),
        error: () => {
          this.unreadCount.set(
            this.activeItems().filter((item) => !item.isRead).length,
          );
        },
      });
  }

  markAllActiveAsRead() {
    const unreadItems = this.activeItems().filter((item) => !item.isRead);

    if (!unreadItems.length || this.isMutating()) {
      return;
    }

    this.isMutating.set(true);
    let pending = unreadItems.length;
    let hasError = false;

    for (const notification of unreadItems) {
      this.api.markRead(notification.id).subscribe({
        next: () => {
          this.patchNotification(notification.id, false, (item) => ({
            ...item,
            isRead: true,
            readAtUtc: item.readAtUtc ?? new Date().toISOString(),
          }));
          pending -= 1;
          if (pending === 0) {
            this.isMutating.set(false);
            if (hasError) {
              this.refreshActive();
            }
            this.refreshUnreadCount();
          }
        },
        error: () => {
          hasError = true;
          pending -= 1;
          if (pending === 0) {
            this.isMutating.set(false);
            this.refreshActive();
            this.refreshUnreadCount();
          }
        },
      });
    }
  }

  markAsRead(notificationId: string) {
    this.isMutating.set(true);
    this.api
      .markRead(notificationId)
      .pipe(finalize(() => this.isMutating.set(false)))
      .subscribe({
        next: () => {
          this.patchNotification(notificationId, false, (item) => ({
            ...item,
            isRead: true,
            readAtUtc: item.readAtUtc ?? new Date().toISOString(),
          }));
          this.refreshUnreadCount();
        },
        error: () => {
          this.refreshActive();
          this.refreshUnreadCount();
        },
      });
  }

  markAsUnread(notificationId: string) {
    this.isMutating.set(true);
    this.api
      .markUnread(notificationId)
      .pipe(finalize(() => this.isMutating.set(false)))
      .subscribe({
        next: () => {
          this.patchNotification(notificationId, false, (item) => ({
            ...item,
            isRead: false,
            readAtUtc: null,
          }));
          this.refreshUnreadCount();
        },
        error: () => {
          this.refreshActive();
          this.refreshUnreadCount();
        },
      });
  }

  archive(notificationId: string) {
    this.isMutating.set(true);
    this.api
      .archive(notificationId)
      .pipe(finalize(() => this.isMutating.set(false)))
      .subscribe({
        next: () => {
          const archivedItem = this.removeFromCollection('active', notificationId);
          if (archivedItem) {
            this.archivedItems.update((items) =>
              [
                this.toViewModel({
                  ...archivedItem,
                  archivedAtUtc: new Date().toISOString(),
                }),
                ...items.filter((item) => item.id !== notificationId),
              ].sort(this.sortNotifications),
            );
            this.activeTotalCount.update((count) => Math.max(0, count - 1));
            this.archivedTotalCount.update((count) => count + 1);
          }
          this.refreshUnreadCount();
        },
        error: () => {
          this.refreshActive();
          this.refreshArchived();
          this.refreshUnreadCount();
        },
      });
  }

  unarchive(notificationId: string) {
    this.isMutating.set(true);
    this.api
      .unarchive(notificationId)
      .pipe(finalize(() => this.isMutating.set(false)))
      .subscribe({
        next: () => {
          const activeItem = this.removeFromCollection('archived', notificationId);
          if (activeItem) {
            this.activeItems.update((items) =>
              [
                this.toViewModel({
                  ...activeItem,
                  archivedAtUtc: null,
                }),
                ...items.filter((item) => item.id !== notificationId),
              ].sort(this.sortNotifications),
            );
            this.activeTotalCount.update((count) => count + 1);
            this.archivedTotalCount.update((count) => Math.max(0, count - 1));
          }
          this.refreshUnreadCount();
        },
        error: () => {
          this.refreshActive();
          this.refreshArchived();
          this.refreshUnreadCount();
        },
      });
  }

  delete(notificationId: string) {
    this.isMutating.set(true);
    this.api
      .delete(notificationId)
      .pipe(finalize(() => this.isMutating.set(false)))
      .subscribe({
        next: () => {
          const removedActive = this.removeFromCollection('active', notificationId);
          const removedArchived = this.removeFromCollection(
            'archived',
            notificationId,
          );

          if (removedActive) {
            this.activeTotalCount.update((count) => Math.max(0, count - 1));
          }

          if (removedArchived) {
            this.archivedTotalCount.update((count) => Math.max(0, count - 1));
          }

          this.refreshUnreadCount();
        },
        error: () => {
          this.refreshActive();
          this.refreshArchived();
          this.refreshUnreadCount();
        },
      });
  }

  openNotification(notification: CustomerNotificationItem) {
    if (!notification.isRead) {
      this.markAsRead(notification.id);
    }

    const target = this.buildNotificationRoute(notification);
    if (target) {
      void this.router.navigate(target);
    }
  }

  private setPageState(
    type: 'active' | 'archived',
    response: CustomerNotificationsResponse,
    replace: boolean,
  ) {
    const mappedItems = response.items.map((item) => this.toViewModel(item));
    const currentItems =
      type === 'active' ? this.activeItems() : this.archivedItems();

    const items = replace
      ? mappedItems
      : this.mergeUniqueNotifications(currentItems, mappedItems);

    if (type === 'active') {
      this.activeItems.set(items);
      this.activePage.set(response.page);
      this.activeTotalCount.set(response.totalCount);
      this.activeLoaded.set(true);
      return;
    }

    this.archivedItems.set(items);
    this.archivedPage.set(response.page);
    this.archivedTotalCount.set(response.totalCount);
    this.archivedLoaded.set(true);
  }

  private mergeUniqueNotifications(
    currentItems: CustomerNotificationItem[],
    incomingItems: CustomerNotificationItem[],
  ) {
    const map = new Map<string, CustomerNotificationItem>();
    for (const item of [...currentItems, ...incomingItems]) {
      map.set(item.id, item);
    }
    return Array.from(map.values()).sort(this.sortNotifications);
  }

  private patchNotification(
    notificationId: string,
    archived: boolean,
    updater: (item: CustomerNotificationItem) => CustomerNotificationItem,
  ) {
    const target = archived ? this.archivedItems : this.activeItems;
    target.update((items) =>
      items.map((item) => (item.id === notificationId ? updater(item) : item)),
    );
  }

  private removeFromCollection(type: 'active' | 'archived', notificationId: string) {
    const source = type === 'active' ? this.activeItems : this.archivedItems;
    const current = source();
    const matched = current.find((item) => item.id === notificationId) ?? null;

    if (!matched) {
      return null;
    }

    source.set(current.filter((item) => item.id !== notificationId));
    return matched;
  }

  private toViewModel(item: CustomerNotificationItemDto): CustomerNotificationItem {
    return {
      ...item,
      tone: this.resolveTone(item),
      timeLabel: this.formatRelativeTime(item.createdAtUtc),
    };
  }

  private resolveTone(item: CustomerNotificationItemDto): CustomerNotificationTone {
    const key = `${item.notificationKey} ${item.actionType ?? ''}`.toLowerCase();

    if (key.includes('coupon') || key.includes('discount')) {
      return 'coupon';
    }

    if (key.includes('gift')) {
      return 'gift';
    }

    if (
      key.includes('ship') ||
      key.includes('delivery') ||
      key.includes('tracking')
    ) {
      return 'delivery';
    }

    return 'order';
  }

  private buildNotificationRoute(notification: CustomerNotificationItem) {
    const { actionType, actionReferenceType, actionReferenceId } = notification;

    if (!actionType || !actionReferenceId?.trim()) {
      return null;
    }

    const actionDefinition = ACTION_MAP[actionType];

    if (!actionDefinition) {
      this.logUnsupportedAction(actionType, actionReferenceType);
      return null;
    }

    if (actionReferenceType !== actionDefinition.referenceType) {
      this.logUnsupportedAction(actionType, actionReferenceType);
      return null;
    }

    return actionDefinition.buildTarget(actionReferenceId);
  }

  private formatRelativeTime(createdAtUtc: string) {
    const createdAt = new Date(createdAtUtc).getTime() + SYRIA_UTC_OFFSET_MS;
    const now = Date.now();
    const diffInSeconds = Math.round((createdAt - now) / 1000);
    const absSeconds = Math.abs(diffInSeconds);
    const formatter = new Intl.RelativeTimeFormat(undefined, {
      numeric: 'auto',
    });

    if (absSeconds < 60) {
      return formatter.format(diffInSeconds, 'second');
    }

    const diffInMinutes = Math.round(diffInSeconds / 60);
    if (Math.abs(diffInMinutes) < 60) {
      return formatter.format(diffInMinutes, 'minute');
    }

    const diffInHours = Math.round(diffInMinutes / 60);
    if (Math.abs(diffInHours) < 24) {
      return formatter.format(diffInHours, 'hour');
    }

    const diffInDays = Math.round(diffInHours / 24);
    if (Math.abs(diffInDays) < 30) {
      return formatter.format(diffInDays, 'day');
    }

    const diffInMonths = Math.round(diffInDays / 30);
    if (Math.abs(diffInMonths) < 12) {
      return formatter.format(diffInMonths, 'month');
    }

    return formatter.format(Math.round(diffInMonths / 12), 'year');
  }

  private sortNotifications(
    left: CustomerNotificationItem,
    right: CustomerNotificationItem,
  ) {
    return (
      new Date(right.createdAtUtc).getTime() -
        new Date(left.createdAtUtc).getTime() || right.id.localeCompare(left.id)
    );
  }

  private resetState() {
    this.activeItems.set([]);
    this.archivedItems.set([]);
    this.unreadCount.set(0);
    this.isActiveLoading.set(false);
    this.isArchivedLoading.set(false);
    this.isUnreadCountLoading.set(false);
    this.isMutating.set(false);
    this.activeLoaded.set(false);
    this.archivedLoaded.set(false);
    this.activePage.set(1);
    this.archivedPage.set(1);
    this.activeTotalCount.set(0);
    this.archivedTotalCount.set(0);
    this.activeError.set('');
    this.archivedError.set('');
    this.lastActiveRequestAt = 0;
    this.lastArchivedRequestAt = 0;
    this.lastUnreadCountRequestAt = 0;
  }

  private logUnsupportedAction(
    actionType: string,
    actionReferenceType: string | null,
  ) {
    console.warn('Unsupported notification action tuple', {
      actionType,
      actionReferenceType,
    });
  }

  private canRequest(type: 'active' | 'archived' | 'unread') {
    const now = Date.now();

    if (type === 'active') {
      return now - this.lastActiveRequestAt >= REQUEST_INTERVAL_MS;
    }

    if (type === 'archived') {
      return now - this.lastArchivedRequestAt >= REQUEST_INTERVAL_MS;
    }

    return now - this.lastUnreadCountRequestAt >= REQUEST_INTERVAL_MS;
  }
}
